/**
 * Teacher Messages Sent - Vista de mensajes enviados por el docente
 * Muestra historial de feedback enviado a estudiantes
 */

import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Mail,
  Calendar,
  User as UserIcon,
  FlaskConical,
  Award,
  Clock,
  Eye,
  CheckCircle2,
  Send,
  Filter,
  Search,
  Inbox
} from 'lucide-react';
import { eplApi } from '../services/epl_api';

const TeacherMessagesSent = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'read', 'unread'
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const response = await eplApi.getSentMessages();
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error cargando mensajes enviados:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
  };

  const filteredMessages = messages.filter(msg => {
    // Filtro por estado de lectura
    if (filter === 'read' && !msg.readAt) return false;
    if (filter === 'unread' && msg.readAt) return false;
    
    // Filtro por búsqueda
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        msg.studentName?.toLowerCase().includes(search) ||
        msg.labName?.toLowerCase().includes(search) ||
        msg.subject?.toLowerCase().includes(search)
      );
    }
    
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Hace un momento';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return `Hace ${Math.floor(diffInHours)} horas`;
    } else if (diffInHours < 48) {
      return 'Ayer';
    } else {
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  };

  const stats = {
    total: messages.length,
    read: messages.filter(m => m.readAt).length,
    unread: messages.filter(m => !m.readAt).length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Send className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Mensajes Enviados
              </h1>
              <p className="text-gray-600 text-sm md:text-base">
                Historial de feedback enviado a estudiantes
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total enviados</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-2xl font-bold text-green-600">{stats.read}</div>
            <div className="text-sm text-gray-600">Leídos</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-2xl font-bold text-orange-600">{stats.unread}</div>
            <div className="text-sm text-gray-600">No leídos</div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por estudiante, laboratorio o asunto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>

            {/* Filter buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'all'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todos ({stats.total})
              </button>
              <button
                onClick={() => setFilter('read')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'read'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Leídos ({stats.read})
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'unread'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                No leídos ({stats.unread})
              </button>
            </div>
          </div>
        </div>

        {/* Messages List */}
        {filteredMessages.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || filter !== 'all' 
                ? 'No se encontraron mensajes' 
                : 'Aún no has enviado mensajes'}
            </h3>
            <p className="text-gray-500">
              {searchTerm || filter !== 'all'
                ? 'Intenta ajustar los filtros de búsqueda'
                : 'Envía feedback personalizado desde los resultados de laboratorios'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredMessages.map((message) => (
              <div
                key={message._id}
                onClick={() => handleViewMessage(message)}
                className="bg-white rounded-lg shadow-sm border p-4 md:p-5 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-start gap-4">
                  
                  {/* Status Icon */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.readAt ? 'bg-green-100' : 'bg-orange-100'
                  }`}>
                    {message.readAt ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-orange-600" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {message.subject}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <UserIcon className="w-3 h-3" />
                          <span>{message.studentName || 'Estudiante'}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(message.sentAt)}
                        </div>
                        {message.readAt ? (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            Leído
                          </span>
                        ) : (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                            No leído
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Lab Info */}
                    <div className="flex items-center gap-3 text-sm mb-2">
                      <div className="flex items-center gap-1 text-gray-600">
                        <FlaskConical className="w-4 h-4 text-blue-600" />
                        <span className="truncate">{message.labName}</span>
                      </div>
                      {message.labScore !== null && (
                        <div className="flex items-center gap-1 text-yellow-600 font-medium">
                          <Award className="w-4 h-4" />
                          <span>{message.labScore}/100</span>
                        </div>
                      )}
                    </div>

                    {/* Preview del mensaje */}
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {message.message}
                    </p>

                    {/* Read timestamp */}
                    {message.readAt && (
                      <div className="flex items-center gap-1 text-xs text-green-600 mt-2">
                        <Eye className="w-3 h-3" />
                        <span>Leído el {new Date(message.readAt).toLocaleDateString('es-ES')}</span>
                      </div>
                    )}

                    {/* Email status */}
                    {message.emailSent && (
                      <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                        <Mail className="w-3 h-3" />
                        <span>Enviado por email</span>
                      </div>
                    )}
                  </div>

                  {/* Action icon */}
                  <div className="flex-shrink-0">
                    <Eye className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de Detalle */}
        {selectedMessage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedMessage(null)}
          >
            <div 
              className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 rounded-t-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Send className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold">{selectedMessage.subject}</h2>
                    <div className="flex items-center gap-2 text-purple-100 text-sm mt-1">
                      <UserIcon className="w-4 h-4" />
                      <span>Para: {selectedMessage.studentName || 'Estudiante'}</span>
                    </div>
                  </div>
                </div>

                {/* Info del Lab */}
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  <div className="flex items-center gap-2">
                    <FlaskConical className="w-4 h-4" />
                    <span>{selectedMessage.labName}</span>
                  </div>
                  {selectedMessage.labScore !== null && (
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4" />
                      <span className="font-semibold">{selectedMessage.labScore}/100</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Enviado: {new Date(selectedMessage.sentAt).toLocaleDateString('es-ES')}</span>
                  </div>
                  {selectedMessage.readAt && (
                    <div className="flex items-center gap-2 bg-green-500/20 px-2 py-1 rounded">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Leído: {new Date(selectedMessage.readAt).toLocaleDateString('es-ES')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                <div className="prose max-w-none">
                  <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {selectedMessage.message}
                  </div>
                </div>

                {selectedMessage.emailSent && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-sm text-blue-700">
                    <Mail className="w-4 h-4" />
                    <span>Este mensaje también fue enviado por correo electrónico</span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4">
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default TeacherMessagesSent;
